import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, FileText, Users, Calendar, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { SEO, pageSEO } from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const requirements = [
  {
    title: 'Academic Requirements',
    items: [
      'High School Diploma or equivalent (WASSCE/WAEC)',
      'Minimum C6 in English and Mathematics',
      'Relevant work experience in health sector (preferred)',
      'Official transcripts from previous institutions',
    ],
  },
  {
    title: 'Documentation',
    items: [
      'Completed application form',
      'Birth certificate or national ID',
      'Passport-sized photographs (4)',
      'Letter of recommendation',
      'Personal statement / Essay',
    ],
  },
];

const steps = [
  { number: '01', title: 'Submit Inquiry', description: 'Fill out the inquiry form below to express your interest.', icon: FileText },
  { number: '02', title: 'Receive Information', description: 'Our admissions team will contact you with detailed info.', icon: Users },
  { number: '03', title: 'Complete Application', description: 'Submit your full application with all required documents.', icon: Calendar },
  { number: '04', title: 'Get Enrolled', description: 'Upon acceptance, complete registration and start your journey.', icon: CheckCircle },
];

const programs = [
  { value: 'bph', label: 'Bachelor of Public Health (BPH)' },
  { value: 'bch', label: 'Bachelor of Community Health (BCH)' },
  { value: 'bsc-sa', label: 'BSc in Sustainable Agriculture' },
  { value: 'bha', label: 'Bachelor of Health Accounting (BHA)' },
  { value: 'bsc-eb', label: 'BSc in Epidemiology and Biostatistics' },
  { value: 'bsc-mlt', label: 'BSc in Medical Laboratory Technology' },
  { value: 'bhep', label: 'Bachelor of Health Education and Promotion' },
  { value: 'bohs', label: 'Bachelor of Occupational Health and Safety' },
  { value: 'bsc-es', label: 'BSc in Environmental Science' },
  { value: 'beh', label: 'Bachelor of Environmental Health' },
  { value: 'bpe', label: 'Bachelor of Primary Education' },
  { value: 'bt', label: 'Bachelor of Teaching' },
  { value: 'bpa', label: 'Bachelor of Public Administration' },
  { value: 'bsc-pm', label: 'BSc in Project Management' },
];

export default function Admissions() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    program_interest: '',
    message: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/inquiries`, { ...formData, inquiry_type: 'admissions' });
      toast.success('Inquiry submitted successfully!', { description: 'Our admissions team will contact you shortly.' });
      setFormData({ full_name: '', email: '', phone: '', program_interest: '', message: '' });
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to submit inquiry', { description: 'Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="admissions-page" className="min-h-screen pt-20">
      <SEO {...pageSEO.admissions} url="/admissions" />
      {/* Hero Section */}
      <section className="relative py-24 bg-[#0D4A2E] pattern-overlay">
        <div className="relative max-w-7xl mx-auto px-4 lg:px-8">
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-4">
            <span className="text-[#C5A028]">Admissions</span>
          </h1>
          <p className="text-white/70 text-xl max-w-2xl">
            Join CEPRES International University and take the first step towards 
            transforming your career in healthcare and public health.
          </p>
        </div>
      </section>

      {/* Process Steps - ALU Style */}
      <section data-testid="process-section" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <h2 className="text-3xl font-bold text-[#0D4A2E] mb-4">Admission Process</h2>
          <p className="text-[#666666] mb-12">Follow these simple steps to join our university</p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0">
            {steps.map((step, index) => (
              <div
                key={step.number}
                data-testid={`step-${step.number}`}
                className="relative p-6 bg-[#F5F5F5] border border-[#E5E5E5]"
              >
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 right-0 w-6 h-0.5 bg-[#C5A028] translate-x-full z-10" />
                )}
                <div className="w-12 h-12 bg-[#0D4A2E] text-white flex items-center justify-center mb-4">
                  <step.icon className="w-6 h-6" />
                </div>
                <span className="text-[#C5A028] font-bold text-sm">{step.number}</span>
                <h3 className="text-lg font-bold text-[#0D4A2E] mt-1 mb-2">{step.title}</h3>
                <p className="text-[#666666] text-sm">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements - ALU Style */}
      <section data-testid="requirements-section" className="py-20 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <h2 className="text-3xl font-bold text-[#0D4A2E] mb-4">Admission Requirements</h2>
          <p className="text-[#666666] mb-12">Ensure you meet these requirements before applying</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {requirements.map((req, index) => (
              <div
                key={req.title}
                data-testid={`requirement-${index}`}
                className="bg-white p-8 border-l-4 border-[#C5A028]"
              >
                <h3 className="text-xl font-bold text-[#0D4A2E] mb-6">{req.title}</h3>
                <ul className="space-y-3">
                  {req.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#C5A028] flex-shrink-0 mt-0.5" />
                      <span className="text-[#666666]">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Inquiry Form - ALU Style */}
      <section data-testid="inquiry-form-section" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Form Info */}
            <div>
              <h2 className="text-3xl font-bold text-[#0D4A2E] mb-2">Submit an Inquiry</h2>
              <p className="text-[#666666] mb-8">
                Interested in joining CIU? Fill out the form and our admissions team 
                will get back to you with all the information you need.
              </p>

              <div className="space-y-6">
                {[
                  { icon: Calendar, title: 'Flexible Start Dates', desc: 'Multiple intake periods throughout the year' },
                  { icon: Users, title: 'Personal Guidance', desc: 'One-on-one support from our admissions team' },
                  { icon: FileText, title: 'Easy Application', desc: 'Streamlined process for working professionals' },
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#F5F5F5] flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-[#C5A028]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#0D4A2E]">{item.title}</h4>
                      <p className="text-[#666666] text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Form */}
            <div className="bg-[#F5F5F5] p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="full_name" className="block text-sm font-medium text-[#0D4A2E] mb-2">Full Name *</label>
                  <input
                    id="full_name"
                    name="full_name"
                    data-testid="input-full-name"
                    value={formData.full_name}
                    onChange={handleChange}
                    required
                    placeholder="Enter your full name"
                    className="w-full px-4 py-3 border border-[#E5E5E5] bg-white focus:border-[#0D4A2E] focus:outline-none transition-colors duration-200"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-[#0D4A2E] mb-2">Email Address *</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    data-testid="input-email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Enter your email"
                    className="w-full px-4 py-3 border border-[#E5E5E5] bg-white focus:border-[#0D4A2E] focus:outline-none transition-colors duration-200"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-[#0D4A2E] mb-2">Phone Number</label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    data-testid="input-phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Enter your phone number"
                    className="w-full px-4 py-3 border border-[#E5E5E5] bg-white focus:border-[#0D4A2E] focus:outline-none transition-colors duration-200"
                  />
                </div>

                <div>
                  <label htmlFor="program_interest" className="block text-sm font-medium text-[#0D4A2E] mb-2">Program of Interest</label>
                  <select
                    id="program_interest"
                    name="program_interest"
                    data-testid="select-program"
                    value={formData.program_interest}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[#E5E5E5] bg-white focus:border-[#0D4A2E] focus:outline-none transition-colors duration-200"
                  >
                    <option value="">Select a program</option>
                    {programs.map((program) => (
                      <option key={program.value} value={program.value}>{program.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-[#0D4A2E] mb-2">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    data-testid="input-message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Tell us about yourself and your goals..."
                    rows={4}
                    className="w-full px-4 py-3 border border-[#E5E5E5] bg-white focus:border-[#0D4A2E] focus:outline-none resize-none transition-colors duration-200"
                  />
                </div>

                <button
                  type="submit"
                  data-testid="submit-inquiry-btn"
                  disabled={isSubmitting}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#C5A028] text-[#1A1A1A] px-8 py-4 font-bold hover:bg-[#D4B43D] transition-colors duration-200 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      SUBMIT INQUIRY
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0D4A2E] text-white pattern-overlay">
        <div className="max-w-4xl mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Have Questions?</h2>
          <p className="text-white/70 mb-8">Our admissions team is here to help you every step of the way.</p>
          <Link 
            to="/contact"
            className="inline-flex items-center gap-2 bg-[#C5A028] text-[#1A1A1A] px-8 py-4 font-bold hover:bg-[#D4B43D] transition-colors duration-200"
          >
            <ArrowRight className="w-5 h-5" />
            CONTACT US
          </Link>
        </div>
      </section>
    </div>
  );
}
